import React, { useState, useEffect } from 'react';
import { Disc3, Plus, X } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DUMMY_DATA = {
    question: 'Spin the wheel to pick who shares next! 🎡',
    segments: ['Alice 🙋', 'Bob 🙋‍♂️', 'Carol 🙋‍♀️', 'David 🙋‍♂️', 'Eve 🙋‍♀️', 'Frank 🙋‍♂️'],
    settings: { description: true, descText: 'Click Spin — whoever the wheel lands on goes next!', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null, allowSpinAgain: true },
};

const SpinnerWheel = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [segments, setSegments] = useState(['Option 1', 'Option 2', 'Option 3']);
    const [settings, setSettings] = useState({
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
        allowSpinAgain: true,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.segments) setSegments(content.segments);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };

    const updateSegment = (index, value) => {
        setSegments(prev => prev.map((s, i) => i === index ? value : s));
    };

    const addSegment = () => {
        setSegments(prev => [...prev, `Option ${prev.length + 1}`]);
    };

    const removeSegment = async (index) => {
        if (segments.length <= 2) return;
        const ok = await confirm({
            title: 'Delete Segment',
            message: 'Delete this segment?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setSegments(prev => prev.filter((_, i) => i !== index));
    };

    // Wheel colors
    const WHEEL_COLORS = [
        '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316',
        '#f59e0b', '#10b981', '#06b6d4', '#3b82f6', '#a855f7',
    ];

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
                <SettingsRow label="Allow spin again" description="Let participants spin again to update their result (off = one spin per person)">
                    <Toggle checked={settings.allowSpinAgain} onChange={(e) => set('allowSpinAgain', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <>
            <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
                {settings.imageUrl && (
                    <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
                )}
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && (
                                <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                            )}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input
                            className={styles.questionInput}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="What should the spinner decide?"
                            autoFocus
                            onBlur={closeQuestionEditor}
                            onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                        />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div
                                className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openQuestionEditor}
                                onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                            >
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && (
                                        <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                    )}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input
                                    className={styles.descInput}
                                    value={settings.descText}
                                    onChange={(e) => set('descText', e.target.value)}
                                    placeholder="Type description here..."
                                    autoFocus
                                    onBlur={closeDescEditor}
                                    onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                                />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div
                                        className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={openDescEditor}
                                        onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                    >
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Segments Editor */}
            <div className={styles.optionsCard}>
                {segments.map((seg, i) => (
                    <div key={i} className={styles.optionRow}>
                        <div
                            className={styles.segmentDot}
                            style={{ '--seg-color': WHEEL_COLORS[i % WHEEL_COLORS.length] }}
                        />
                        <input
                            className={styles.optionInput}
                            value={seg}
                            onChange={(e) => updateSegment(i, e.target.value)}
                            placeholder={`Segment ${i + 1}`}
                        />
                        {segments.length > 2 && (
                            <Tooltip title="Remove segment" placement="top">
                                <button
                                    className={styles.optionRemoveBtn}
                                    onClick={() => removeSegment(i)}
                                    aria-label="Remove segment"
                                >
                                    <X size={14} />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                ))}
                <div className={styles.optionsFooter}>
                    <button className={styles.addOptionBtn} onClick={addSegment}>
                        <Plus size={14} /> Add segment
                    </button>
                </div>
            </div>

            {/* Wheel Preview */}
            <div className={styles.wheelPreview}>
                <svg viewBox="0 0 200 200" width="220" height="220">
                    {segments.map((seg, i) => {
                        const angle = 360 / segments.length;
                        const startAngle = i * angle - 90;
                        const endAngle = startAngle + angle;
                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = (endAngle * Math.PI) / 180;
                        const x1 = 100 + 90 * Math.cos(startRad);
                        const y1 = 100 + 90 * Math.sin(startRad);
                        const x2 = 100 + 90 * Math.cos(endRad);
                        const y2 = 100 + 90 * Math.sin(endRad);
                        const largeArc = angle > 180 ? 1 : 0;
                        const midAngle = ((startAngle + endAngle) / 2 * Math.PI) / 180;
                        const labelX = 100 + 55 * Math.cos(midAngle);
                        const labelY = 100 + 55 * Math.sin(midAngle);
                        const color = WHEEL_COLORS[i % WHEEL_COLORS.length];

                        return (
                            <g key={i}>
                                <path
                                    d={`M100,100 L${x1},${y1} A90,90 0 ${largeArc},1 ${x2},${y2} Z`}
                                    fill={color}
                                    stroke="#fff"
                                    strokeWidth="1.5"
                                />
                                <text
                                    x={labelX}
                                    y={labelY}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="#fff"
                                    fontSize={segments.length > 6 ? "6" : "7.5"}
                                    fontWeight="600"
                                >
                                    {seg.length > 10 ? seg.slice(0, 9) + '…' : seg}
                                </text>
                            </g>
                        );
                    })}
                    {/* Center circle */}
                    <circle cx="100" cy="100" r="14" fill="#fff" stroke="#e2e8f0" strokeWidth="2" />
                    <circle cx="100" cy="100" r="4" fill="#6366f1" />
                    {/* Pointer */}
                    <polygon points="100,6 95,18 105,18" fill="#ef4444" stroke="#fff" strokeWidth="1" />
                </svg>
            </div>
        </>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setSegments(DUMMY_DATA.segments);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'spinner-wheel',
                title: question || 'Spinner Wheel',
                content: { question, segments, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<Disc3 size={20} />}
            color="var(--cat-pink)"
            title="Spinner Wheel"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                saveContent({ question, segments, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default SpinnerWheel;
